import { useState } from 'react';

export default function ImportTab({ onImportSuccess }) {
    const [ingredientFile, setIngredientFile] = useState(null);
    const [dishFile, setDishFile] = useState(null);
    const [ingredientLoading, setIngredientLoading] = useState(false);
    const [dishLoading, setDishLoading] = useState(false);
    const [ingredientMessage, setIngredientMessage] = useState('');
    const [dishMessage, setDishMessage] = useState('');

    const getCsrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content;

    const parseJsonResponse = async (response) => {
        const text = await response.text();
        try {
            return JSON.parse(text);
        } catch {
            return { message: text || 'Réponse invalide du serveur.' };
        }
    };

    const handleImportIngredients = async (e) => {
        e.preventDefault();
        if (!ingredientFile) {
            setIngredientMessage('Veuillez sélectionner un fichier');
            return;
        }

        const csrfToken = getCsrfToken();
        if (!csrfToken) {
            setIngredientMessage('Jeton CSRF introuvable. Rechargez la page et réessayez.');
            return;
        }

        setIngredientLoading(true);
        setIngredientMessage('');

        const formData = new FormData();
        formData.append('file', ingredientFile);

        try {
            const response = await fetch('/api/import/ingredients', {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'X-CSRF-TOKEN': csrfToken,
                },
                body: formData,
            });

            const data = await parseJsonResponse(response);

            if (!response.ok) {
                setIngredientMessage(`Erreur : ${data.message || 'Import échoué'}`);
            } else {
                setIngredientMessage(`✓ ${data.message || 'Ingrédients importés avec succès'}`);
                setIngredientFile(null);
                document.getElementById('ingredientFile').value = '';
                onImportSuccess?.();
            }
        } catch (error) {
            console.error('Erreur import ingrédients:', error);
            setIngredientMessage('Erreur lors de l\'import');
        } finally {
            setIngredientLoading(false);
        }
    };

    const handleImportDishes = async (e) => {
        e.preventDefault();
        if (!dishFile) {
            setDishMessage('Veuillez sélectionner un fichier');
            return;
        }

        setDishLoading(true);
        setDishMessage('');

        const formData = new FormData();
        formData.append('file', dishFile);

        const csrfToken = getCsrfToken();
        if (!csrfToken) {
            setDishMessage('Jeton CSRF introuvable. Rechargez la page et réessayez.');
            setDishLoading(false);
            return;
        }

        try {
            const response = await fetch('/api/import/dishes', {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'X-CSRF-TOKEN': csrfToken,
                },
                body: formData,
            });

            const data = await parseJsonResponse(response);

            if (!response.ok) {
                setDishMessage(`Erreur : ${data.message || 'Import échoué'}`);
            } else {
                setDishMessage(`✓ ${data.message || 'Plats importés avec succès'}`);
                setDishFile(null);
                document.getElementById('dishFile').value = '';
                onImportSuccess?.();
            }
        } catch (error) {
            console.error('Erreur import plats:', error);
            setDishMessage('Erreur lors de l\'import');
        } finally {
            setDishLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            <section className="rounded-3xl border border-green-400 bg-green-50/90 p-5 shadow-xl shadow-slate-950/20">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <p className="text-sm uppercase tracking-[0.35em] text-green-400">Import de données</p>
                        <h2 className="text-3xl font-semibold text-green-900">Importer depuis CSV</h2>
                    </div>
                    <p className="max-w-xl text-sm text-green-600">
                        Importez des ingrédients et plats à partir de fichiers CSV. Les données manquantes seront créées automatiquement.
                    </p>
                </div>
            </section>

            <div className="grid gap-6 md:grid-cols-2">
                {/* Import Ingrédients */}
                <div className="rounded-3xl border border-green-400 bg-white p-6 shadow-xl shadow-slate-950/20">
                    <h3 className="mb-4 text-xl font-semibold text-green-900">Importer Ingrédients</h3>
                    <p className="mb-4 text-sm text-slate-600">
                        Format CSV : Produit, Marque, Caracteristique, Fournisseur, Prix achat, Quantité, Unité
                    </p>

                    <form onSubmit={handleImportIngredients} className="space-y-4">
                        <div>
                            <input
                                id="ingredientFile"
                                type="file"
                                accept=".csv"
                                onChange={(e) => setIngredientFile(e.target.files?.[0])}
                                className="block w-full text-sm text-slate-500 file:rounded-3xl file:border-0 file:bg-green-600 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-green-500"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={ingredientLoading}
                            className="w-full rounded-3xl bg-green-600 px-4 py-3 font-semibold text-white hover:bg-green-500 disabled:opacity-50"
                        >
                            {ingredientLoading ? 'Import en cours...' : 'Importer Ingrédients'}
                        </button>
                        {ingredientMessage && (
                            <p className={`text-sm ${ingredientMessage.startsWith('✓') ? 'text-green-600' : 'text-red-600'}`}>
                                {ingredientMessage}
                            </p>
                        )}
                    </form>
                </div>

                {/* Import Plats */}
                <div className="rounded-3xl border border-green-400 bg-white p-6 shadow-xl shadow-slate-950/20">
                    <h3 className="mb-4 text-xl font-semibold text-green-900">Importer Plats avec Recette</h3>
                    <p className="mb-4 text-sm text-slate-600">
                        Format CSV : Nom du plat, Prix, Ingrédient 1, Quantité 1, Unité 1, Ingrédient 2, Quantité 2, Unité 2...
                    </p>

                    <form onSubmit={handleImportDishes} className="space-y-4">
                        <div>
                            <input
                                id="dishFile"
                                type="file"
                                accept=".csv"
                                onChange={(e) => setDishFile(e.target.files?.[0])}
                                className="block w-full text-sm text-slate-500 file:rounded-3xl file:border-0 file:bg-green-600 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-green-500"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={dishLoading}
                            className="w-full rounded-3xl bg-green-600 px-4 py-3 font-semibold text-white hover:bg-green-500 disabled:opacity-50"
                        >
                            {dishLoading ? 'Import en cours...' : 'Importer Plats'}
                        </button>
                        {dishMessage && (
                            <p className={`text-sm ${dishMessage.startsWith('✓') ? 'text-green-600' : 'text-red-600'}`}>
                                {dishMessage}
                            </p>
                        )}
                    </form>
                </div>
            </div>

            {/* Instructions */}
            <div className="rounded-3xl border border-yellow-300 bg-yellow-50 p-6">
                <h4 className="mb-3 font-semibold text-yellow-900">Instructions</h4>
                <ul className="list-disc list-inside space-y-2 text-sm text-yellow-800">
                    <li>Assurez-vous que le fichier CSV utilise le bon encodage (UTF-8)</li>
                    <li>Les en-têtes de colonne doivent correspondre exactement au format attendu</li>
                    <li>Les lignes avec des données manquantes seront ignorées</li>
                    <li>Les produits/plats existants ne seront pas mis à jour</li>
                </ul>
            </div>
        </div>
    );
}
